import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { getCatalogVersion } from '../api/catalog.api';
import { catalogKeys } from '../api/catalog.keys';

const POLL_MS = 20_000;

/**
 * Keeps an open storefront tab honest about admin edits.
 *
 * Polls a tiny stamp that moves whenever a product, setting, testimonial, or
 * review changes. When it moves, every mounted catalog query refetches — React
 * Query only refetches what is actually on screen, so a shopper on a product
 * page fetches that product, not the whole catalog.
 */
export function useCatalogVersion() {
  const queryClient = useQueryClient();
  const seen = useRef(null);

  const { data: version, error } = useQuery({
    queryKey: catalogKeys.version(),
    queryFn: ({ signal }) => getCatalogVersion(signal),
    refetchInterval: POLL_MS,
    // A backgrounded tab has no one looking at it — stop polling until focus.
    refetchIntervalInBackground: false,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });

  useEffect(() => {
    if (error) console.warn('Catalog version poll failed:', error);
  }, [error]);

  useEffect(() => {
    if (version === undefined) return;

    // The first stamp is the baseline, not a change.
    if (seen.current === null) {
      seen.current = version;
      return;
    }

    if (seen.current !== version) {
      seen.current = version;
      queryClient.invalidateQueries({ queryKey: catalogKeys.all });
    }
  }, [version, queryClient]);
}
